import request from '@/utils/request';
import {listingDrupalTransform, listingSolr, contentNodesSolr} from '@/utils/transforms';

/**
 * Returns single listings from the backend by uuid.
 *
 * @param id
 *   Drupal UUID of the listing.
 *
 * @returns {Promise<any>}
 */
export const getFullListingInfo = (id, bundle) => new Promise((resolve, reject) => {
  let q = {
    'include': 'domSrc,homeStatus,slides,slides.imageFile,image,image.imageFile,video,listingRef,listingRef.houseFiles,listingRef.houseFiles.docFile,listingRef.commTerm,listingRef.commTerm.slides.imageFile',
    'fields[sfhomestatus]': 'id,internalId,path,title',
    'fields[documents]': 'docFile',
    'fields[videosRemote]': 'videoEmbed',
    'fields[videosLocal]': 'videoFile,internalId,uuid,thumbnail',
    'fields[imagesMls]': 'imageFile',
    'fields[files]': 'uri',
  };
  let url = `/listings/${id}`;
  if (bundle == 'listing') {
    q = {
      'include': 'domSrc, commTerm',
      'fields[sfhouse]': 'id,internalId,address,bathAll,beds,sqft,homeLot,homesite,commTerm,title',
    }
    url = `/sfhouse/${id}`;
  }

  request('api')
      .get(url)
      .query(q)
      // Tell superagent to consider any valid Drupal response as successful.
      // Later we can capture error codes if needed.
      .ok(resp => resp.statusCode)
      .then((response) => {
        resolve({
          page_data: response.statusCode === 200 ? listingDrupalTransform(response.body.data) : {},
          statusCode: response.statusCode,
        });
      })
      .catch((error) => {
        console.error('Could not fetch the listing.', error);
        reject(error);
      });
});

/**
 * Returns single listings from the backend by uuid.
 *
 * @param id
 *   Drupal UUID of the listing.
 *
 * @returns {Promise<any>}
 */
// export const getFullRentalInfo = id => new Promise((resolve, reject) => {
//   request('api')
//     .get(`/listings/${id}`)
//     .query({
//       'include': 'slides,slides.imageFile,image,image.imageFile,user_ref,user_ref.image.imageFile,city_term,city_term.slides.imageFile,commTerm,commTerm.slides.imageFile,neighborhood,neighborhood.slides.imageFile' +
//         ',elemSchool,highSchool,midSchool',
//       'fields[users]': 'internalId,id,mail,roles,public_email,name,full_name,image,image.imageFile,address,subtitle,agentType',
//       'fields[images]': 'imageFile',
//       'fields[imagesMls]': 'imageFile',
//       'fields[files]': 'uri',
//     })
//     // Tell superagent to consider any valid Drupal response as successful.
//     // Later we can capture error codes if needed.
//     .ok(resp => resp.statusCode)
//     .then((response) => {
//       resolve({
//         page_data: response.statusCode === 200 ? listingDrupalTransform(response.body.data) : {},
//         statusCode: response.statusCode,
//       });
//     })
//     .catch((error) => {
//       console.error('Could not fetch the listing.', error);
//       reject(error);
//     });
// });

/**
 * Returns listings search resultset from Solr.
 *
 * @param pageLimit
 *   Listings per page.
 *
 * @param pageOffset
 *   Amount of listings to skip from the beginning (for pagination).
 *
 * @returns {Promise<any>}
 */
export const listingsSolrSearch = async (url, pageLimit, pageOffset, q, solr_index) => new Promise((resolve, reject) => {
  let query = 'wt=json&';

  url = url || '/api/solr';
  pageLimit = pageLimit || 24;
  pageOffset = pageOffset || 0;
  q = q || 'q=*:*&fl=*';
  solr_index = solr_index || 'solrdrupal1';

  query += q + '&rows=' + pageLimit + '&start=' + pageOffset;

  request('solr', true)
    // Via POST (safer, as the GET query might hit length limit):
      .post(url)
      .send({solr_path: 'select', query: query, secret: process.env.SECRET_API_TOKEN, solr_index: solr_index})
        // Tell superagent to consider any valid Drupal response as successful.
      .ok((resp) => {
        return resp.statusCode;
      })
      .then((response) => {
        let new_data = (q.includes('fq=ss_content_type:escapia_unit')) ? response.body.docs.map(node => listingSolr(node)) : response.body.docs.map(node => contentNodesSolr(node));
        resolve({
          page_data: response.statusCode === 200 && response.body?.numFound > 0 ? new_data : [],
          ttlResults: response.statusCode === 200 ? response.body?.numFound : false,
          ttlPages: (response.statusCode === 200 && response.body?.numFound) ? Math.ceil(response.body?.numFound / pageLimit) : false,
          hasNextPage: response.statusCode === 200 && response.body?.numFound && response.body?.docs?.length > response.body?.numFound,
          // hasPrevPage: !!response.body.links.prev,
          statusCode: response.statusCode,
          initial: false,
        });
      })
      .catch((error) => {
        console.error('Could not fetch list of listings.', error);
        reject(error);
      });
});

/**
 * Solr More Like This search query.
 *
 * @param pageLimit
 *   Listings per page.
 *
 * @param pageOffset
 *   Amount of listings to skip from the beginning (for pagination).
 *
 * @returns {Promise<any>}
 */
export const listingsSolrMLTSearch = async (url, pageLimit, pageOffset, q, solr_index) => new Promise((resolve, reject) => {
  let query = 'wt=json&omitHeader=true&json.nl=flat&mlt.mintf=1&mlt.mindf=1&mlt.interestingTerms=none&mlt.match.include=false&mlt.match.offset=0';
  // https://public-master.solrcluster.com/solr/tunnell_live/mlt?wt=json&omitHeader=true&json.nl=flat&mlt.mintf=1&mlt.mindf=1&mlt.interestingTerms=none&mlt.match.include=false&mlt.match.offset=0&mlt.fl=tm_X3b_en_city&q=id:%225cwkwn-eat_listings-entity:node/639:en%22

  url = url || '/api/solr';
  pageLimit = pageLimit || 24;
  pageOffset = pageOffset || 0;
  q = q || '';
  solr_index = solr_index || 'solrdrupal1';

  query += q + '&rows=' + pageLimit + '&start=' + pageOffset;
  request('solr', true)
      .get('/mlt')
      .query(query)
      // Tell superagent to consider any valid Drupal response as successful.
      // Later we can capture error codes if needed.
      //   .ok(resp => resp.statusCode)
      .ok((resp) => {
        return resp.statusCode;
      })
      .then((response) => {
        resolve({
          listings: response.statusCode === 200 && response.body.response?.numFound > 0 ? response.body.response.docs.map(listing => listingSolr(listing)) : [],
          ttlResults: response.statusCode === 200 ? response.body.response?.numFound : false,
          ttlPages: response.statusCode === 200 && response.body.response?.numFound ? Math.ceil(response.body.response?.numFound / pageLimit) : false,
          hasNextPage: response.statusCode === 200 && response.body?.response?.numFound && response.body?.response?.docs?.length > response.body.response.numFound,
          // hasPrevPage: !!response.body.links.prev,
          statusCode: response.statusCode,
          initial: false,
        });
      })
      .catch((error) => {
        console.error('listingsSolrMLTSearch: Could not fetch list of listings.', error);
        reject(error);
      });
});
